import { createTheme } from '@mui/material/styles';
import type { TypeBackground, TypeText } from '@mui/material/styles';

// HAVEN Official Brand Colors (authentic brand palette)
const havenColors = {
  // Primary brand colors - OFFICIAL HAVEN PALETTE
  primaryTeal: '#00C4C1',    // "Blewstream" - HAVEN's signature teal
  primaryYellow: '#FFE623',  // "Yellowflow" - HAVEN's signature yellow
  
  // IAQ Status colors from pro portal
  iaqGood: '#58eac7',
  iaqFair: '#e5ba00', 
  iaqPoor: '#f36262',
  
  // Supporting colors
  darkTeal: '#008B8A',
  lightTeal: '#33D4D1',
  // Teal for text/links on light backgrounds. The brand teal (#00C4C1) only
  // hits ~2.2:1 contrast on white — this darker shade clears WCAG AA (4.5:1)
  // while still reading as HAVEN teal. Use for foreground text, not fills.
  tealText: '#0A6E6C',
  lightYellow: '#FFF8CC',
  darkGray: '#2C2C2C',
  secondaryGrey: '#535F66', // Navigation and secondary text color
  lightGray: '#F5F5F5',
  white: '#FFFFFF',
};

// Custom theme extending MUI defaults
export const havenTheme = createTheme({
  palette: {
    primary: {
      main: havenColors.primaryTeal,
      dark: havenColors.darkTeal,
      light: havenColors.lightTeal,
      contrastText: havenColors.white,
    },
    secondary: {
      main: havenColors.primaryYellow,
      dark: '#E6CF00',
      light: '#FFF04D',
      contrastText: havenColors.darkGray,
    },
    error: {
      main: havenColors.iaqPoor,
    },
    warning: {
      main: havenColors.iaqFair,
    },
    success: {
      main: havenColors.iaqGood,
    },
    background: {
      default: havenColors.white,
      paper: havenColors.white,
    } as TypeBackground,
    text: {
      primary: havenColors.darkGray,
      secondary: '#666666',
    } as TypeText,
    grey: {
      50: '#F9F9F9',
      100: havenColors.lightGray,
      200: '#E0E0E0',
      300: '#C4C4C4',
      400: '#A8A8A8',
      500: '#8C8C8C',
      600: '#707070',
      700: '#545454',
      800: '#383838',
      900: havenColors.darkGray,
    },
  },
  typography: {
    fontFamily: 'Rubik, "Helvetica", "Arial", sans-serif',
    // Headings are FLUID. Previously every level had a single fixed size, so an
    // h1 rendered at the same 48px+ on a 1440px desktop and a 393px phone —
    // which is why hero headlines broke into two oversized lines on mobile.
    // Desktop sizes are unchanged; the mobile step is new.
    //
    // Weights here are now actually applied. A `fontWeight: 500 !important`
    // rule in MuiCssBaseline used to override all six levels, so every heading
    // rendered at 500 and the weight hierarchy declared below did nothing.
    // Negative tracking on the large sizes stops big Rubik from reading loose.
    h1: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
      '@media (min-width:600px)': { fontSize: '2.5rem' },
      '@media (min-width:900px)': { fontSize: '3rem' },
    },
    h2: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.25,
      letterSpacing: '-0.015em',
      '@media (min-width:600px)': { fontSize: '2.125rem' },
      '@media (min-width:900px)': { fontSize: '2.5rem' },
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
      '@media (min-width:600px)': { fontSize: '1.75rem' },
      '@media (min-width:900px)': { fontSize: '2rem' },
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.4,
      '@media (min-width:900px)': { fontSize: '1.5rem' },
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 500,
      lineHeight: 1.5,
      '@media (min-width:900px)': { fontSize: '1.25rem' },
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none' as const,
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 4, // Match pro-portal's default (MUI default is 4)
  },
  spacing: 8,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: 'Rubik, "Helvetica", "Arial", sans-serif',
          fontWeight: 400,
          fontSize: '1rem',
          lineHeight: 1.5,
          letterSpacing: '0.00938em',
        },
        // boxSizing only. The previous rule also set
        // `fontFamily: '...!important'` on every element, which is a blunt
        // instrument: it blocks any deliberate font choice (monospace, icon
        // fonts) and cannot be overridden by a component that needs to. The
        // family is inherited from `body` above, which is enough.
        '*': {
          boxSizing: 'border-box',
        },
        // NOTE: no `fontWeight` here on purpose. This block used to carry
        // `fontWeight: '500 !important'`, which silently overrode every weight
        // in the typography scale — h1 and h6 are declared 600 but rendered
        // 500, so headings differed only by size and the page read flat.
        // Weight now comes from the typography scale.
        'h1, h2, h3, h4, h5, h6': {
          fontFamily: 'Rubik, "Helvetica", "Arial", sans-serif',
        },
      },
    },
    // Custom component styling
    MuiLink: {
      styleOverrides: {
        // The brand teal #00C4C1 fails WCAG AA on white (~2.2:1). Use the
        // darker tealText for link text everywhere a color isn't explicitly
        // set. Links that pass color="inherit" / color="white" / etc. are
        // unaffected.
        root: {
          color: havenColors.tealText,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '1rem',
          fontWeight: 500,
          variants: [
            // Keep accessible text colors and HAVEN gradients after MUI 9
            // removes the combined variant/color style override keys.
            { props: { variant: 'text', color: 'primary' }, style: { color: havenColors.tealText } },
            { props: { variant: 'outlined', color: 'primary' }, style: {
              color: havenColors.tealText,
              borderColor: havenColors.tealText,
              '&:hover': { borderColor: havenColors.darkTeal, backgroundColor: 'rgba(10, 110, 108, 0.04)' },
            } },
            { props: { variant: 'contained', color: 'primary' }, style: {
              background: `linear-gradient(135deg, ${havenColors.primaryTeal} 0%, ${havenColors.lightTeal} 100%)`,
              boxShadow: '0 4px 14px 0 rgba(0, 196, 193, 0.39)',
              '&:hover': {
                background: `linear-gradient(135deg, ${havenColors.darkTeal} 0%, ${havenColors.primaryTeal} 100%)`,
                boxShadow: '0 6px 20px 0 rgba(0, 196, 193, 0.5)',
              },
            } },
            { props: { variant: 'contained', color: 'secondary' }, style: {
              background: `linear-gradient(135deg, ${havenColors.primaryYellow} 0%, #FFF04D 100%)`,
              color: havenColors.darkGray,
              '&:hover': { background: 'linear-gradient(135deg, #E6CF00 0%, #FFE623 100%)' },
            } },
          ],
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'white', // Updated to white as per layout requirements
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid #F0F0F0',
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          boxSizing: 'border-box',
        },
        container: {
          boxSizing: 'border-box',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxSizing: 'border-box',
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: { root: { minWidth: 56 } },
    },
  },
});

// Export colors for use in components
export { havenColors };
